import React, { useEffect, useState } from 'react';
import { db } from '../../firebase/firebaseConfig';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { ArrowLeft, X, Copy, Check, User, Home, ChevronRight } from 'lucide-react';

const ACCOUNT_FIELD_ORDER = [
  'email', 'name', 'nameAtOrder', 'accountType', 'accountStatus',
  'primaryFamilyID', 'families', 'numOfFamilies', 'numOfOwningFamilies', 'numOfInvitedFamilies',
  'batch', 'orderCode', 'shippingCountryAtOrder', 'shippingAddressAtOrder',
  'shipped', 'shippedAt', 'preparingForShipping', 'shippingAddressesConfirmed',
  'receiveEmailUpdates', 'trackingNumbers', 'deliveryDates',
  'setupCompletedAt',
];

const FAMILY_FIELD_ORDER = [
  'name', 'firstSignin', 'lastSignin',
];

// Fields whose values are family IDs (clickable)
const FAMILY_ID_FIELDS = new Set(['families', 'primaryFamilyID']);

function formatTimestamp(ts) {
  try {
    const date = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
    const formatted = date.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
    const diffMs = Date.now() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    let relative;
    if (diffMinutes < 60) relative = `${diffMinutes}m ago`;
    else if (diffHours < 24) relative = `${diffHours}h ago`;
    else relative = `${diffDays}d ago`;
    return `${formatted} (${relative})`;
  } catch {
    return String(ts);
  }
}

function isFirestoreTimestamp(value) {
  return value && (typeof value.toDate === 'function' || (typeof value.seconds === 'number' && typeof value.nanoseconds === 'number'));
}

function sortFields(data, preferredOrder) {
  const entries = Object.entries(data);
  const orderMap = {};
  preferredOrder.forEach((key, i) => { orderMap[key] = i; });
  return entries.sort((a, b) => {
    const aIdx = orderMap[a[0]] ?? 9999;
    const bIdx = orderMap[b[0]] ?? 9999;
    if (aIdx !== bIdx) return aIdx - bIdx;
    return a[0].localeCompare(b[0]);
  });
}

function FieldValue({ fieldKey, value, onNavigateToFamily }) {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground">—</span>;
  }

  if (typeof value === 'boolean') {
    return (
      <span className={value ? 'text-green-600' : 'text-red-500'}>
        {value ? 'true' : 'false'}
      </span>
    );
  }

  if (isFirestoreTimestamp(value)) {
    return <span>{formatTimestamp(value)}</span>;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    if (FAMILY_ID_FIELDS.has(fieldKey) && typeof value === 'string' && value.length > 0) {
      return (
        <span
          className="text-blue-600 hover:underline cursor-pointer"
          onClick={() => onNavigateToFamily(value)}
        >
          {value}
        </span>
      );
    }
    return <span className="break-all">{String(value)}</span>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-muted-foreground">[] (empty)</span>;
    const isFamilyArray = FAMILY_ID_FIELDS.has(fieldKey);
    return (
      <div className="space-y-0.5">
        {value.map((item, i) => (
          <div key={i}>
            {isFamilyArray && typeof item === 'string' ? (
              <span
                className="text-blue-600 hover:underline cursor-pointer"
                onClick={() => onNavigateToFamily(item)}
              >
                {item}
              </span>
            ) : typeof item === 'object' && item !== null ? (
              <pre className="text-xs bg-muted/50 rounded p-1 whitespace-pre-wrap">{JSON.stringify(item, null, 2)}</pre>
            ) : (
              <span>{String(item)}</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === 'object') {
    return (
      <pre className="text-xs bg-muted/50 rounded p-1.5 whitespace-pre-wrap max-h-40 overflow-y-auto">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }

  return <span>{String(value)}</span>;
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      className="ml-2 p-0.5 rounded hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors"
      title="Copy to clipboard"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

export default function DocumentViewer({ initialType, initialId, onClose }) {
  const [viewStack, setViewStack] = useState([{ type: initialType, id: initialId }]);
  const [docData, setDocData] = useState(null);
  const [subcollections, setSubcollections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const current = viewStack[viewStack.length - 1];

  useEffect(() => {
    let cancelled = false;
    const fetchDoc = async () => {
      setLoading(true);
      setError(null);
      setDocData(null);
      setSubcollections(null);
      try {
        const collectionName = current.type === 'account' ? 'accountsInfo' : 'familyInfo';
        const snap = await getDoc(doc(db, collectionName, current.id));
        if (cancelled) return;
        if (!snap.exists()) {
          setError('Document not found');
          setLoading(false);
          return;
        }
        setDocData(snap.data());

        // For families, try fetching subcollections (may fail due to permissions)
        if (current.type === 'family') {
          const subs = {};
          const results = await Promise.allSettled([
            getDocs(collection(db, 'familyInfo', current.id, 'igloos')),
            getDocs(collection(db, 'familyInfo', current.id, 'connectedFamilies')),
          ]);
          if (cancelled) return;
          const [iglooResult, connectedResult] = results;
          if (iglooResult.status === 'fulfilled' && !iglooResult.value.empty) {
            subs.igloos = iglooResult.value.docs.map(d => ({ id: d.id, ...d.data() }));
          }
          if (connectedResult.status === 'fulfilled' && !connectedResult.value.empty) {
            subs.connectedFamilies = connectedResult.value.docs.map(d => ({ id: d.id, ...d.data() }));
          }
          if (Object.keys(subs).length > 0) setSubcollections(subs);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
      if (!cancelled) setLoading(false);
    };
    fetchDoc();
    return () => { cancelled = true; };
  }, [current.type, current.id]);

  const navigateToFamily = (familyId) => {
    setViewStack(prev => [...prev, { type: 'family', id: familyId }]);
  };

  const goBack = () => {
    setViewStack(prev => prev.slice(0, -1));
  };

  const fieldOrder = current.type === 'account' ? ACCOUNT_FIELD_ORDER : FAMILY_FIELD_ORDER;
  const sortedFields = docData ? sortFields(docData, fieldOrder) : [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card className="w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0 border-b">
          <div className="flex items-center gap-2">
            {viewStack.length > 1 && (
              <Button variant="ghost" size="sm" onClick={goBack} className="h-7 w-7 p-0">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <CardTitle className="flex items-center gap-2 text-base">
              {current.type === 'account' ? <User className="w-4 h-4" /> : <Home className="w-4 h-4" />}
              {current.type === 'account' ? 'Account Info' : 'Family Info'}
            </CardTitle>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto pt-4">
          {/* Document ID */}
          <div className="mb-4 p-2 bg-muted/50 rounded flex items-center">
            <span className="text-xs text-muted-foreground mr-2">ID:</span>
            <span className="text-sm font-mono flex-1 truncate">{current.id}</span>
            <CopyButton text={current.id} />
          </div>

          {/* Breadcrumb */}
          {viewStack.length > 1 && (
            <div className="mb-3 flex items-center gap-1 text-xs text-muted-foreground flex-wrap">
              {viewStack.map((item, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <ChevronRight className="w-3 h-3" />}
                  <span className={i === viewStack.length - 1 ? 'text-foreground font-medium' : 'cursor-pointer hover:underline'}
                    onClick={i < viewStack.length - 1 ? () => setViewStack(prev => prev.slice(0, i + 1)) : undefined}
                  >
                    {item.type === 'account' ? 'Account' : 'Family'}: {item.id.slice(0, 8)}...
                  </span>
                </React.Fragment>
              ))}
            </div>
          )}

          {loading && (
            <div className="text-sm text-muted-foreground text-center py-8">Loading...</div>
          )}

          {error && (
            <div className="text-sm text-red-500 text-center py-8">{error}</div>
          )}

          {docData && (
            <div className="space-y-1.5">
              {sortedFields.map(([key, value]) => (
                <div key={key} className="flex gap-3 py-1 border-b border-muted/30 last:border-0">
                  <span className="w-44 flex-shrink-0 text-xs text-muted-foreground font-medium pt-0.5 truncate" title={key}>
                    {key}
                  </span>
                  <div className="text-sm flex-1 min-w-0">
                    <FieldValue fieldKey={key} value={value} onNavigateToFamily={navigateToFamily} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Subcollections for family docs */}
          {subcollections && (
            <div className="mt-6 space-y-4">
              {subcollections.igloos && (
                <div>
                  <h4 className="text-sm font-medium mb-2 text-muted-foreground">Igloos ({subcollections.igloos.length})</h4>
                  <div className="space-y-2">
                    {subcollections.igloos.map(igloo => (
                      <div key={igloo.id} className="p-2 bg-muted/30 rounded text-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-xs">{igloo.id}</span>
                          <CopyButton text={igloo.id} />
                        </div>
                        {Object.entries(igloo).filter(([k]) => k !== 'id').map(([k, v]) => (
                          <div key={k} className="flex gap-2 text-xs">
                            <span className="text-muted-foreground w-32 flex-shrink-0">{k}</span>
                            <span className="break-all">{isFirestoreTimestamp(v) ? formatTimestamp(v) : JSON.stringify(v)}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {subcollections.connectedFamilies && (
                <div>
                  <h4 className="text-sm font-medium mb-2 text-muted-foreground">Connected Families ({subcollections.connectedFamilies.length})</h4>
                  <div className="space-y-2">
                    {subcollections.connectedFamilies.map(cf => (
                      <div key={cf.id} className="p-2 bg-muted/30 rounded text-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className="font-mono text-xs text-blue-600 hover:underline cursor-pointer"
                            onClick={() => navigateToFamily(cf.id)}
                          >
                            {cf.id}
                          </span>
                          <CopyButton text={cf.id} />
                        </div>
                        {Object.entries(cf).filter(([k]) => k !== 'id').map(([k, v]) => (
                          <div key={k} className="flex gap-2 text-xs">
                            <span className="text-muted-foreground w-32 flex-shrink-0">{k}</span>
                            <span className="break-all">{isFirestoreTimestamp(v) ? formatTimestamp(v) : JSON.stringify(v)}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
